'use strict';

import request from 'superagent';
import api from './core';
import {cachePromise, d, cmpStr} from '../utils';
import {getAllCompaniesMap} from './companies';

const cache = {
    allDocTypes: undefined,
    allDocTypesChoices: undefined,
    reusableDocTypesChoices: undefined
};

export function emptyDocumentType() {
    return {
        id: undefined,
        name: undefined,
        function: 'none',
        partner_ids: [],
        company: null,
        contract_type: null,
        reusable: false,
        archived: false,
        template: null
    };
}

export function fetchPage(page = 1, q = '', archived = '') {
    return api.prepare(request.get(api.url('/document_types/'))).query({page, q, archived});
}

export function list(page = 1, q = '', archived = '') {
    return api.forwardResult(
        fetchPage(page, q, archived),
        'results'
    );
}

export function create(document_type) {
    return api.forwardResult(
        api.prepare(request.post(api.url('/document_types/?archived'))).send(document_type),
        'id'
    );
}

export function fetch(id) {
    return api.forwardResult(
        api.prepare(request.get(api.url(`/document_types/${id}/?archived`))), 'id');
}

export function update(document_type) {
    const id = encodeURIComponent(document_type.id);
    return api.forwardResult(
        api.prepare(request.put(api.url(`/document_types/${id}/?archived`))).send(document_type),
        'id'
    );
}

export async function listAll() {
    let next = 1;
    const out = [];

    while (next) {
        const page = (await fetchPage(next)).body;

        if (page.next) {
            next += 1;
        } else {
            next = false;
        }

        out.push(...page.results);
    }

    return out;
}

export const listAllCached = cachePromise(listAll, cache, 'allDocTypes');

export function clearListCache() {
    cache.allDocTypes = undefined;
    cache.reusableDocTypesChoices = undefined;
}

export async function docsListToChoices(docs) {
    const companiesMap = await getAllCompaniesMap();

    return docs.map((x) => ({
        id: x.id,
        name: x.name,
        company: d(() => companiesMap[x.company].name),
        type: d(() => companiesMap[x.company].types[x.contract_type].name)
    })).sort((a, b) => {
        let cmp = cmpStr(a.company, b.company);

        if (cmp !== 0) {
            return cmp;
        }

        cmp = cmpStr(a.type, b.type);

        if (cmp !== 0) {
            return cmp;
        }

        return cmpStr(a.name, b.name);
    }).map((dt) => {
        const parts = [];

        if (dt.company) {
            parts.push(dt.company);
        }

        if (dt.type) {
            parts.push(dt.type);
        }

        if (dt.name) {
            parts.push(dt.name);
        }

        return {
            label: parts.join('\u00a0—\u00a0'),
            value: `${dt.id}`
        };
    });
}

export async function docTypeChoices(onlyReusable) {
    return await docsListToChoices(
        (await listAllCached()).filter((x) => x.reusable || !onlyReusable)
    );
}

export const reusableDocTypeChoices = cachePromise(
    () => docTypeChoices(true),
    cache,
    'reusableDocTypesChoices'
);

export const allDocTypeChoices = cachePromise(
    () => docTypeChoices(false),
    cache,
    'allDocTypesChoices'
);

export async function forContract(contract, docIds, requireTemplate = false) {
    const allDocs = await listAll();

    const idsIdx = {};
    docIds.forEach((id) => idsIdx[id] = true);

    return allDocs.filter((doc) => {
        if (requireTemplate && !doc.template) {
            return;
        }

        if (doc.company && doc.company !== contract.company) {
            return false;
        }

        return !(doc.contract_type && doc.contract_type !== contract.type);
    });
}

/**
 * List of potential partners for which we can store IDs. The reference of that
 * list is in the Python model (of Gescomi).
 */
export function partnerIds() {
    return [
        {value: 'generali_vie', label: 'Generali Vie'},
        {value: 'generali_per', label: 'Generali PER'},
        {value: 'activeseed', label: 'ActiveSeed'},
        {value: 'paper', label: 'Papier'}
    ];
}

/**
 * List of potential functions that a document can have. The reference of that
 * list is in the Python model (of Gescomi).
 */
export function functions() {
    return [
        {value: 'none', label: '—'},
        {value: 'id', label: 'Document d\'identité'},
        {value: 'proof_of_address', label: 'Justificatif de domicile'},
        {value: 'bank_id', label: 'RIB'}
    ];
}



// WEBPACK FOOTER //
// ./src/api/document_types.js